import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  IconButton,
  Alert,
  Tabs,
  Tab,
  Stack,
  useTheme,
  useMediaQuery,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AssignmentIcon from "@mui/icons-material/Assignment";
import BlockIcon from "@mui/icons-material/Block";
import MoreVertIcon from "@mui/icons-material/MoreVert";

import useWorkspaceNavigation from "../hooks/useWorkspaceNavigation";
import { useAppDispatch, useAppSelector } from "../redux/store";
import {
  getCreditNoteById,
  resetCreditNoteServerStatus,
  voidCreditNote,
} from "../redux/actions/creditNoteActions";
import ConfirmDialog from "../components/common/ConfirmDialog";
import CreditNoteDetailsTab from "../components/creditNotes/CreditNoteDetailsTab";
import CreditNotePdfTab from "../components/creditNotes/CreditNotePdfTab";
import ApplyToInvoiceModal from "../components/creditNotes/ApplyToInvoiceModal";
import AppCircularProgress from "../components/common/AppCircularProgress";
import AppButton from "../components/buttons/AppButton";
import StatusChip from "../components/common/StatusChip";

const CreditNotePage: React.FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { closeCurrentAndNavigate } = useWorkspaceNavigation();

  const {
    item: currentCreditNote,
    loadingFetchOne: loadingCreditNote,
    errorFetchOne: errorLoadingCreditNote,
    serverStatus,
    loadingVoid: loadingVoidCreditNote,
  } = useAppSelector((state) => state.creditNote);

  const [activeTab, setActiveTab] = useState<number>(0);
  const [noteId, setNoteId] = useState<string | null>(null);
  const [isApplyOpen, setIsApplyOpen] = useState<boolean>(false);
  const [isVoidOpen, setIsVoidOpen] = useState<boolean>(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);

  useEffect(() => {
    const uuidRegex =
      /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
    const match = location.pathname.match(uuidRegex);

    if (match && match[0]) {
      setNoteId(match[0]);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (noteId) dispatch(getCreditNoteById(noteId));
  }, [dispatch, noteId]);

  useEffect(() => {
    if (serverStatus?.status === 200) {
      setIsApplyOpen(false);
      setIsVoidOpen(false);
      dispatch(resetCreditNoteServerStatus());
    }
  }, [serverStatus, dispatch]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>): void =>
    setMenuAnchorEl(event.currentTarget);
  const handleMenuClose = (): void => setMenuAnchorEl(null);

  if (loadingCreditNote) return <AppCircularProgress />;
  if (errorLoadingCreditNote)
    return <Alert severity="error">{errorLoadingCreditNote}</Alert>;
  if (!currentCreditNote) return null;

  const isVoided = currentCreditNote.status === "VOID";
  const isOpen = currentCreditNote.status === "OPEN";

  // --- RENDER FUNCTIONS FOR ACTIONS ---
  const renderDesktopActions = (): React.JSX.Element => (
    <Stack direction="row" spacing={2}>
      {isOpen && currentCreditNote.customerId && (
        <AppButton
          pill
          color="secondary"
          variant="contained"
          startIcon={<AssignmentIcon />}
          onClick={() => setIsApplyOpen(true)}
        >
          Apply to Invoice
        </AppButton>
      )}
      {!isVoided && (
        <AppButton
          pill
          variant="outlined"
          color="error"
          startIcon={<BlockIcon />}
          onClick={() => setIsVoidOpen(true)}
        >
          Void Note
        </AppButton>
      )}
    </Stack>
  );

  const renderMobileActions = (): React.JSX.Element => (
    <Box>
      <IconButton onClick={handleMenuOpen} color="inherit">
        <MoreVertIcon />
      </IconButton>
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
      >
        {isOpen && currentCreditNote.customerId && (
          <MenuItem
            onClick={() => {
              setIsApplyOpen(true);
              handleMenuClose();
            }}
          >
            <ListItemIcon>
              <AssignmentIcon fontSize="small" color="secondary" />
            </ListItemIcon>
            <ListItemText primary="Apply to Invoice" />
          </MenuItem>
        )}
        {!isVoided && (
          <MenuItem
            onClick={() => {
              setIsVoidOpen(true);
              handleMenuClose();
            }}
          >
            <ListItemIcon>
              <BlockIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText primary="Void Note" />
          </MenuItem>
        )}
      </Menu>
    </Box>
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        bgcolor: "background.default",
      }}
    >
      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          pt: 3,
          pb: 1,
          bgcolor: "background.paper",
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Stack
          direction="row"
          sx={{ justifyContent: "space-between", alignItems: "center", mb: 2 }}
        >
          <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
            <IconButton
              onClick={() => closeCurrentAndNavigate("/credit-notes")}
              color="inherit"
              size="small"
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography
              variant={isMobile ? "h6" : "h5"}
              sx={{ fontWeight: "bold" }}
            >
              Credit Note #{currentCreditNote.noteNumber}
            </Typography>
            {!isMobile && <StatusChip status={currentCreditNote.status} />}
          </Stack>

          {/* Conditional Actions based on screen size */}
          {isMobile ? renderMobileActions() : renderDesktopActions()}
        </Stack>

        {isMobile && (
          <Box sx={{ mb: 2, pl: 5 }}>
            <StatusChip status={currentCreditNote.status} />
          </Box>
        )}

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ ml: { xs: 0, sm: 6 }, mb: 2 }}
        >
          Issued: {new Date(currentCreditNote.issueDate).toLocaleDateString()} •{" "}
          {currentCreditNote.branchName}
        </Typography>

        <Tabs
          value={activeTab}
          onChange={(_e, v) => setActiveTab(v)}
          indicatorColor="primary"
          textColor="primary"
          variant={isMobile ? "fullWidth" : "standard"}
        >
          <Tab label="Credit Note Details" />
          <Tab label="PDF Document" />
        </Tabs>
      </Box>

      <Box sx={{ flex: 1, overflowY: "auto", p: { xs: 1.5, sm: 3 } }}>
        {activeTab === 0 && <CreditNoteDetailsTab note={currentCreditNote} />}
        {activeTab === 1 && <CreditNotePdfTab note={currentCreditNote} />}
      </Box>

      {isApplyOpen && (
        <ApplyToInvoiceModal
          open={isApplyOpen}
          onClose={() => setIsApplyOpen(false)}
          creditNote={currentCreditNote}
        />
      )}
      <ConfirmDialog
        open={isVoidOpen}
        onClose={() => setIsVoidOpen(false)}
        onConfirm={() =>
          dispatch(voidCreditNote(currentCreditNote.id, "User requested void"))
        }
        title="Void Credit Note"
        description="Are you sure you want to void this credit note? This action cannot be reversed."
        confirmText="Void"
        loading={loadingVoidCreditNote}
      />
    </Box>
  );
};

export default CreditNotePage;
